import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCourses } from '../hooks/useCourses';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { format } from 'date-fns';
import { ja, enUS, pt, id, vi, es } from 'date-fns/locale';
import { CalendarIcon, CheckCircle, Send, Loader2, AlertCircle, User, LogIn, UserPlus, Plus, Trash2, Camera, Upload, ImageIcon, Copy } from 'lucide-react';
import NationalitySelect from '../components/NationalitySelect';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PhotoUploadField({ label, value, uploading, onUpload, onRemove, testId }) {
  return (
    <div>
      <span className="text-xs text-gray-500 mb-1 block">{label}</span>
      {value ? (
        <div className="relative border rounded-lg overflow-hidden bg-white">
          <img src={`${BACKEND_URL}${value}`} alt={label} className="w-full h-20 object-cover" />
          <button type="button" className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600" onClick={onRemove}>
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div className="flex gap-1">
          <label className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#00A651] hover:bg-green-50 transition-colors bg-white">
            {uploading ? <Loader2 className="h-3 w-3 animate-spin text-[#00A651]" /> : <><Upload className="h-3 w-3 text-gray-400" /><span className="text-xs text-gray-500">File</span></>}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files[0])} data-testid={testId} />
          </label>
          <label className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#00A651] hover:bg-green-50 transition-colors bg-white">
            {uploading ? <Loader2 className="h-3 w-3 animate-spin text-[#00A651]" /> : <><Camera className="h-3 w-3 text-gray-400" /><span className="text-xs text-gray-500">Camera</span></>}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onUpload(e.target.files[0])} />
          </label>
        </div>
      )}
    </div>
  );
}

export default function Apply() {
  const { t, language } = useLanguage();
  const { user, isAuthenticated, loading: authLoading, getAuthHeaders } = useAuth();
  const { courses, loading: coursesLoading } = useCourses();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedCourseId = searchParams.get('course');
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [participantError, setParticipantError] = useState(false);
  useEffect(() => {
    if (participantError && participants.some(p => (p.name || '').trim())) setParticipantError(false);
  }, [participants, participantError]);
  const [formData, setFormData] = useState({
    name: '',
    nameKana: '',
    email: '',
    phone: '',
    company: '',
    courseId: preselectedCourseId || '',
    courseOption: '',
    scheduleId: '',
    experience: 'no',
    message: '',
  });

  const COURSE_OPT_L10N = {
    select: { ja:'コースを選択', en:'Select Course Type', pt:'Selecione o tipo de curso', id:'Pilih Jenis Kursus', vi:'Chọn loại khóa học', es:'Seleccione el tipo de curso', tl:'Piliin ang Uri ng Kurso', zh:'选择课程类型' },
    suffix: { ja:'コース', en:' Course', pt:' Curso', id:' Kursus', vi:' Khóa', es:' Curso', tl:' Kurso', zh:'课程' },
    theory: { ja:'学科', en:'Theory', pt:'Teoria', id:'Teori', vi:'Lý thuyết', es:'Teoría', tl:'Teorya', zh:'学科' },
    selfAttend: { ja:'自分が受講する', en:'I will attend', pt:'Eu vou participar', id:'Saya akan ikut', vi:'Tôi sẽ tham gia', es:'Yo asistiré', tl:'Ako ang dadalo', zh:'本人参加' },
    selfAdded: { ja:'あなたを受講者に登録しました', en:'You have been added as a participant', pt:'Você foi adicionado como participante', id:'Anda telah ditambahkan sebagai peserta', vi:'Bạn đã được thêm làm học viên', es:'Se le ha añadido como participante', tl:'Naidagdag ka bilang kalahok', zh:'已将您登记为学员' },
    selfExists: { ja:'あなたはすでに受講者に登録されています', en:'You are already registered as a participant', pt:'Você já está registrado como participante', id:'Anda sudah terdaftar sebagai peserta', vi:'Bạn đã được đăng ký làm học viên', es:'Ya está registrado como participante', tl:'Nakarehistro ka na bilang kalahok', zh:'您已登记为学员' },
    noParticipant: { ja:'受講者を1名以上登録してください（氏名は必須）', en:'Please register at least one participant (name required)', pt:'Cadastre pelo menos um participante (nome obrigatório)', id:'Daftarkan minimal satu peserta (nama wajib diisi)', vi:'Vui lòng đăng ký ít nhất một học viên (bắt buộc nhập họ tên)', es:'Registre al menos un participante (nombre obligatorio)', tl:'Magrehistro ng kahit isang kalahok (kailangan ang pangalan)', zh:'请至少登记一名学员（姓名为必填项）' },
    practical: { ja:'実技', en:'Practical', pt:'Prática', id:'Praktik', vi:'Thực hành', es:'Práctica', tl:'Praktika', zh:'实技' },
    total: { ja:'合計', en:'Total', pt:'Total', id:'Total', vi:'Tổng', es:'Total', tl:'Kabuuan', zh:'合计' },
    required: { ja:'コースを選択してください', en:'Please select a course type', pt:'Selecione o tipo de curso', id:'Silakan pilih jenis kursus', vi:'Vui lòng chọn loại khóa học', es:'Seleccione el tipo de curso', tl:'Pumili ng uri ng kurso', zh:'请选择课程类型' },
  };
  const L = (k) => (COURSE_OPT_L10N[k]?.[language] || COURSE_OPT_L10N[k]?.ja || '');

  // Load user data into form when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        nameKana: user.nameKana || prev.nameKana,
        email: user.email || prev.email,
        phone: user.phone || prev.phone,
        company: user.company || prev.company,
      }));
    }
  }, [isAuthenticated, user]);

  // Fetch schedules
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const response = await axios.get(`${API}/schedules`);
        // Filter only open schedules
        const openSchedules = (response.data || []).filter(s => s.status === 'open');
        setSchedules(openSchedules);
      } catch (error) {
        console.error('Error fetching schedules:', error);
      } finally {
        setSchedulesLoading(false);
      }
    };
    fetchSchedules();
  }, []);

  const localeMap = {
    ja: ja,
    en: enUS,
    pt: pt,
    id: id,
    vi: vi,
    es: es,
    tl: enUS,
  };

  // Get available schedules for selected course
  const availableSchedules = schedules.filter(s => s.courseId === formData.courseId);

  // Course options (A/B/C) for the selected course
  const selectedCourseObj = courses.find(c => c.id === formData.courseId);
  const enabledCourseOptions = (selectedCourseObj?.courseOptions || []).filter(o => o && o.enabled);

  // Get selected schedule details
  const selectedSchedule = schedules.find(s => s.id === formData.scheduleId);

  const formatScheduleDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'yyyy/MM/dd', { locale: localeMap[language] || ja });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCourseChange = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      courseId: value,
      courseOption: '',
      scheduleId: '' // Reset schedule when course changes
    }));
  };

  // Participant management
  const addParticipant = () => {
    if (participants.length >= 15) {
      toast.error(t('applyPage.maxParticipants'));
      return;
    }
    setParticipants(prev => [...prev, { name: '', birthDate: '', gender: '', nationality: '', language: language, postalCode: '', address: '', idDocumentFront: '', idDocumentBack: '', qualificationFront: '', qualificationBack: '' }]);
  };

  const userAsParticipant = (p) => ({
      ...p,
      name: user.name || '',
      birthDate: user.birthDate || '',
      gender: user.gender || '',
      nationality: user.nationality || '',
      language: user.language || language,
      postalCode: user.postalCode || '',
      address: user.address || '',
      idDocumentFront: user.idDocumentFront || p.idDocumentFront || '',
      idDocumentBack: user.idDocumentBack || p.idDocumentBack || '',
      qualificationFront: user.certDocumentFront || p.qualificationFront || '',
      qualificationBack: user.certDocumentBack || p.qualificationBack || '',
  });

  const copyUserToParticipant = (index) => {
    if (!user) return;
    setParticipants(prev => prev.map((p, i) => i === index ? userAsParticipant(p) : p));
    toast.success(language === 'ja' ? 'ユーザー情報を反映しました' : 'User info applied');
  };

  // 本人を受講者に登録（空欄の受講者があればそこへ、なければ追加）
  const addSelfAsParticipant = () => {
    if (!user) return;
    const nn = (v) => (v || '').replace(/[\s　]/g, '');
    if (participants.some(p => nn(p.name) && nn(p.name) === nn(user.name))) {
      toast.info(L('selfExists'));
      return;
    }
    const emptyIdx = participants.findIndex(p => !(p.name || '').trim());
    if (emptyIdx < 0 && participants.length >= 15) {
      toast.error(t('applyPage.maxParticipants'));
      return;
    }
    const blank = { name: '', birthDate: '', gender: '', nationality: '', language: language, postalCode: '', address: '', idDocumentFront: '', idDocumentBack: '', qualificationFront: '', qualificationBack: '' };
    setParticipants(prev => emptyIdx >= 0 ? prev.map((p, i) => i === emptyIdx ? userAsParticipant(p) : p) : [...prev, userAsParticipant(blank)]);
    toast.success(L('selfAdded'));
  };

  const removeParticipant = (index) => {
    setParticipants(prev => prev.filter((_, i) => i !== index));
  };

  const updateParticipantName = (index, name) => {
    setParticipants(prev => prev.map((p, i) => i === index ? { ...p, name } : p));
  };

  const updateParticipantField = (index, field, value) => {
    setParticipants(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };

  const handleParticipantFileUpload = async (index, field, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('register.onlyImages'));
      return;
    }
    setUploadingIdx(`${index}-${field}`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/applications/upload-participant-doc`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setParticipants(prev => prev.map((p, i) => i === index ? { ...p, [field]: res.data.path } : p));
      toast.success(t('register.uploaded'));
    } catch (err) {
      toast.error(t('register.uploadFailed'));
    } finally {
      setUploadingIdx(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (enabledCourseOptions.length > 0 && !formData.courseOption) {
      toast.error(L('required'));
      return;
    }
    if (!participants.some(p => (p.name || '').trim())) {
      setParticipantError(true);
      toast.error(L('noParticipant'));
      document.querySelector('[data-testid="participants-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setIsSubmitting(true);

    try {
      const selectedCourse = courses.find(c => c.id === formData.courseId);
      const applicationData = {
        ...formData,
        courseName: selectedCourse ? (selectedCourse.name[language] || selectedCourse.name.ja) : '',
        scheduleName: selectedSchedule ? `${formatScheduleDate(selectedSchedule.startDate)} - ${formatScheduleDate(selectedSchedule.endDate)}` : '',
        preferredDate: selectedSchedule ? selectedSchedule.startDate : null,
        language: language,
        submittedAt: new Date().toISOString(),
        participants: participants.filter(p => p.name.trim() !== ''),
      };

      await axios.post(`${API}/applications`, applicationData, {
        headers: getAuthHeaders()
      });
      
      setIsSubmitted(true);
      toast.success(t('apply.success'));
    } catch (error) {
      console.error('Application submission error:', error);
      toast.error(t('applyPage.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="apply-success">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="w-16 h-16 bg-[#e6f6ec] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-[#00A651]" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t('applyPage.applicationReceived')}
          </h1>
          <p className="text-gray-600 mb-6">
            {t('apply.success')}
          </p>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/mypage')}
              className="w-full bg-[#00A651] hover:bg-[#008a43]"
            >
              {t('applyPage.goToMyPage')}
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/')}
              className="w-full"
            >
              {t('applyPage.returnHome')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show login/register prompt if not authenticated
  if (!authLoading && !isAuthenticated) {
    const selectedCourse = courses.find(c => c.id === preselectedCourseId);
    
    return (
      <div className="min-h-screen bg-gray-50" data-testid="apply-login-required">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('apply.title')}
            </h1>
            <p className="text-gray-600 max-w-2xl">
              {t('apply.subtitle')}
            </p>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {/* Course info if preselected */}
            {selectedCourse && (
              <div className="mb-6 p-4 bg-[#e6f6ec] rounded-lg border border-[#00A651]/20">
                <p className="text-sm text-gray-600 mb-1">
                  {t('applyPage.selectedCourse')}
                </p>
                <p className="font-semibold text-gray-900">
                  {selectedCourse.name[language] || selectedCourse.name.ja}
                </p>
                <p className="text-[#00A651] font-bold">
                  ¥{new Intl.NumberFormat('ja-JP').format(selectedCourse.price)}
                </p>
              </div>
            )}

            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="h-8 w-8 text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {t('applyPage.loginRequired')}
              </h2>
              <p className="text-gray-600">
                {t('applyPage.loginRequiredDesc')}
              </p>
            </div>

            <div className="space-y-4">
              <Link 
                to={`/register?redirect=/apply${preselectedCourseId ? `?course=${preselectedCourseId}` : ''}&mode=login`}
                className="block"
              >
                <Button className="w-full bg-[#00A651] hover:bg-[#008a43] py-6">
                  <LogIn className="h-5 w-5 mr-2" />
                  {t('applyPage.loginButton')}
                </Button>
              </Link>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-4 text-gray-500">
                    {t('applyPage.or')}
                  </span>
                </div>
              </div>

              <Link 
                to={`/register?redirect=/apply${preselectedCourseId ? `?course=${preselectedCourseId}` : ''}`}
                className="block"
              >
                <Button variant="outline" className="w-full py-6 border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-white">
                  <UserPlus className="h-5 w-5 mr-2" />
                  {t('applyPage.registerButton')}
                </Button>
              </Link>
            </div>

            <p className="text-center text-sm text-gray-500 mt-6">
              {t('applyPage.registerBenefit')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A651]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="apply-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('apply.title')}
          </h1>
          <p className="text-gray-600 max-w-2xl">
            {t('apply.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Logged in user notice */}
        {isAuthenticated && user && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <User className="h-5 w-5 text-green-600" />
            <p className="text-green-800 text-sm">
              {language === 'ja' 
                ? `${user.name}${t('applyPage.loggedInAs')}`
                : `Logged in as ${user.name}${t('applyPage.loggedInAs')}`}
            </p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8">
          <div className="space-y-6">
            {/* Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-gray-700 font-medium">
                  {t('apply.form.name')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="mt-1"
                  data-testid="input-name"
                />
              </div>
              <div>
                <Label htmlFor="nameKana" className="text-gray-700 font-medium">
                  {t('apply.form.nameKana')}
                </Label>
                <Input
                  id="nameKana"
                  name="nameKana"
                  value={formData.nameKana}
                  onChange={handleInputChange}
                  className="mt-1"
                  data-testid="input-name-kana"
                />
              </div>
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="text-gray-700 font-medium">
                  {t('apply.form.email')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="mt-1"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-gray-700 font-medium">
                  {t('apply.form.phone')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="mt-1"
                  data-testid="input-phone"
                />
              </div>
            </div>

            {/* Company */}
            <div>
              <Label htmlFor="company" className="text-gray-700 font-medium">
                {t('apply.form.company')}
              </Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="mt-1"
                data-testid="input-company"
              />
            </div>

            {/* Participants Section */}
            <div className={`rounded-xl border-2 p-4 sm:p-5 shadow-sm transition-colors ${participantError ? 'border-red-500 bg-red-50 ring-4 ring-red-200' : 'border-[#00A651] bg-[#f2fbf6]'}`} data-testid="participants-section">
              {participantError && (
                <div className="mb-3 flex items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white" role="alert" data-testid="participants-error">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {L('noParticipant')}
                </div>
              )}
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#00A651] text-white">
                    <User className="h-5 w-5" />
                  </span>
                  <div>
                    <Label className="text-gray-900 font-bold text-lg">
                      {t('applyPage.participantInfo')}
                    </Label>
                    <p className="text-sm text-gray-700 mt-0.5">
                      {t('applyPage.participantDesc')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 min-w-0 max-w-full">
                  {user && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addSelfAsParticipant}
                      className="border-2 border-[#00A651] text-[#00A651] bg-white hover:bg-[#e6f6ec] font-semibold"
                      data-testid="add-self-participant-btn"
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      {L('selfAttend')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    onClick={addParticipant}
                    disabled={participants.length >= 15}
                    className="bg-[#00A651] hover:bg-[#008a43] text-white font-semibold shadow-sm"
                    data-testid="add-participant-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('applyPage.addParticipant')}
                  </Button>
                </div>
              </div>

              {participants.length === 0 && (
                <button
                  type="button"
                  onClick={addParticipant}
                  className="w-full text-center py-8 px-3 bg-white rounded-lg border-2 border-dashed border-[#00A651]/60 hover:bg-[#e6f6ec] transition-colors"
                  data-testid="participants-empty-add"
                >
                  <span className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-[#e6f6ec] text-[#00A651] animate-pulse">
                    <Plus className="h-6 w-6" />
                  </span>
                  <span className="block text-sm font-medium text-[#00A651]">
                    {t('applyPage.clickAddParticipant')}
                  </span>
                </button>
              )}

              <div className="space-y-4">
                {participants.map((participant, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white relative" data-testid={`participant-${idx}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-[#00A651] whitespace-nowrap shrink-0">
                        {t('applyPage.participantLabel')} {idx + 1}
                      </span>
                      <div className="flex items-center gap-1">
                        {isAuthenticated && user && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-[#00A651] border-[#00A651] hover:bg-[#00A651] hover:text-white h-7 text-xs px-2"
                            onClick={() => copyUserToParticipant(idx)}
                            data-testid={`copy-user-to-participant-btn-${idx}`}
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            {language === 'ja' ? 'ユーザー情報を自動反映' : 'Auto-fill my info'}
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                          onClick={() => removeParticipant(idx)}
                          data-testid={`remove-participant-${idx}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Participant Name */}
                    <div className="mb-3">
                      <Label className="text-sm text-gray-600">
                        {t('applyPage.participantName')} <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={participant.name}
                        onChange={(e) => updateParticipantName(idx, e.target.value)}
                        placeholder={t('applyPage.participantNamePlaceholder')}
                        className="mt-1 bg-white"
                        data-testid={`participant-name-${idx}`}
                      />
                    </div>

                    {/* Birth Date & Gender */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                      <div>
                        <Label className="text-sm text-gray-600">
                          {language === 'ja' ? '生年月日' : 'Date of Birth'}
                        </Label>
                        <Input
                          type="date"
                          value={participant.birthDate || ''}
                          onChange={(e) => updateParticipantField(idx, 'birthDate', e.target.value)}
                          className="mt-1 bg-white"
                          data-testid={`participant-birthdate-${idx}`}
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">
                          {language === 'ja' ? '性別' : 'Gender'}
                        </Label>
                        <select
                          value={participant.gender || ''}
                          onChange={(e) => updateParticipantField(idx, 'gender', e.target.value)}
                          className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                          data-testid={`participant-gender-${idx}`}
                        >
                          <option value="">{language === 'ja' ? '選択してください' : 'Select'}</option>
                          <option value="male">{language === 'ja' ? '男性' : 'Male'}</option>
                          <option value="female">{language === 'ja' ? '女性' : 'Female'}</option>
                        </select>
                      </div>
                    </div>

                    {/* Nationality & Postal Code */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                      <div>
                        <Label className="text-sm text-gray-600">
                          {language === 'ja' ? '国籍' : 'Nationality'}
                        </Label>
                        <div className="mt-1">
                          <NationalitySelect
                            value={participant.nationality || ''}
                            onChange={(val) => {
                              const langMap = {
                                'Japan': 'ja', '日本': 'ja',
                                'Brazil': 'pt', 'ブラジル': 'pt', 'Portugal': 'pt',
                                'United States': 'en', 'アメリカ': 'en', 'United Kingdom': 'en', 'Australia': 'en', 'Canada': 'en', 'Philippines': 'tl', 'フィリピン': 'tl',
                                'Vietnam': 'vi', 'ベトナム': 'vi',
                                'Indonesia': 'id', 'インドネシア': 'id',
                                'Spain': 'es', 'スペイン': 'es', 'Mexico': 'es', 'メキシコ': 'es', 'Peru': 'es', 'ペルー': 'es', 'Bolivia': 'es', 'ボリビア': 'es', 'Colombia': 'es', 'Argentina': 'es',
                                'China': 'zh', '中国': 'zh', 'Korea': 'ja', '韓国': 'ja',
                              };
                              const autoLang = langMap[val] || participants[idx]?.language || language;
                              updateParticipantField(idx, 'nationality', val);
                              updateParticipantField(idx, 'language', autoLang);
                            }}
                            language={language}
                            dataTestId={`participant-nationality-${idx}`}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">
                          {language === 'ja' ? '教材言語' : language === 'pt' ? 'Idioma do Material' : language === 'es' ? 'Idioma del Material' : language === 'vi' ? 'Ngon ngu tai lieu' : language === 'id' ? 'Bahasa Materi' : language === 'tl' ? 'Wika ng Materyal' : language === 'zh' ? '教材语言' : 'Material Language'}
                        </Label>
                        <select
                          value={participant.language || language}
                          onChange={(e) => updateParticipantField(idx, 'language', e.target.value)}
                          className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                          data-testid={`participant-language-${idx}`}
                        >
                          <option value="ja">日本語</option>
                          <option value="en">English</option>
                          <option value="pt">Português</option>
                          <option value="es">Español</option>
                          <option value="vi">Tiếng Việt</option>
                          <option value="id">Bahasa Indonesia</option>
                          <option value="tl">Tagalog</option>
                          <option value="zh">中文</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">
                          {language === 'ja' ? '郵便番号' : 'Postal Code'}
                        </Label>
                        <Input
                          value={participant.postalCode || ''}
                          onChange={(e) => updateParticipantField(idx, 'postalCode', e.target.value)}
                          placeholder="123-4567"
                          className="mt-1 bg-white"
                          data-testid={`participant-postalcode-${idx}`}
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div className="mb-3">
                      <Label className="text-sm text-gray-600">
                        {language === 'ja' ? '住所' : 'Address'}
                      </Label>
                      <Input
                        value={participant.address || ''}
                        onChange={(e) => updateParticipantField(idx, 'address', e.target.value)}
                        placeholder={language === 'ja' ? '都道府県 市区町村 番地' : 'Full address'}
                        className="mt-1 bg-white"
                        data-testid={`participant-address-${idx}`}
                      />
                    </div>

                    {/* Photo Uploads - 2x2 grid */}
                    <div className="space-y-3">
                      {/* ID Document */}
                      <div>
                        <Label className="text-sm text-gray-600 font-medium">{t('applyPage.idDocumentPhoto')}</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                          {/* ID Front */}
                          <PhotoUploadField
                            label={language === 'ja' ? '表面' : 'Front'}
                            value={participant.idDocumentFront}
                            uploading={uploadingIdx === `${idx}-idDocumentFront`}
                            onUpload={(file) => handleParticipantFileUpload(idx, 'idDocumentFront', file)}
                            onRemove={() => setParticipants(prev => prev.map((p, i) => i === idx ? { ...p, idDocumentFront: '' } : p))}
                            testId={`participant-id-front-${idx}`}
                          />
                          {/* ID Back */}
                          <PhotoUploadField
                            label={language === 'ja' ? '裏面' : 'Back'}
                            value={participant.idDocumentBack}
                            uploading={uploadingIdx === `${idx}-idDocumentBack`}
                            onUpload={(file) => handleParticipantFileUpload(idx, 'idDocumentBack', file)}
                            onRemove={() => setParticipants(prev => prev.map((p, i) => i === idx ? { ...p, idDocumentBack: '' } : p))}
                            testId={`participant-id-back-${idx}`}
                          />
                        </div>
                      </div>
                      {/* Qualification */}
                      <div>
                        <Label className="text-sm text-gray-600 font-medium">{t('applyPage.qualificationPhoto')}</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                          {/* Qual Front */}
                          <PhotoUploadField
                            label={language === 'ja' ? '表面' : 'Front'}
                            value={participant.qualificationFront}
                            uploading={uploadingIdx === `${idx}-qualificationFront`}
                            onUpload={(file) => handleParticipantFileUpload(idx, 'qualificationFront', file)}
                            onRemove={() => setParticipants(prev => prev.map((p, i) => i === idx ? { ...p, qualificationFront: '' } : p))}
                            testId={`participant-qual-front-${idx}`}
                          />
                          {/* Qual Back */}
                          <PhotoUploadField
                            label={language === 'ja' ? '裏面' : 'Back'}
                            value={participant.qualificationBack}
                            uploading={uploadingIdx === `${idx}-qualificationBack`}
                            onUpload={(file) => handleParticipantFileUpload(idx, 'qualificationBack', file)}
                            onRemove={() => setParticipants(prev => prev.map((p, i) => i === idx ? { ...p, qualificationBack: '' } : p))}
                            testId={`participant-qual-back-${idx}`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {participants.length > 0 && participants.length < 15 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addParticipant}
                  className="mt-3 w-full border-dashed border-gray-300 text-gray-500 hover:text-[#00A651] hover:border-[#00A651]"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t('applyPage.addParticipantCount')} ({participants.length}/15)
                </Button>
              )}
            </div>

            {/* Course Selection */}
            <div>
              <Label htmlFor="courseId" className="text-gray-700 font-medium">
                {t('apply.form.course')} <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={formData.courseId} 
                onValueChange={handleCourseChange}
              >
                <SelectTrigger className="mt-1 relative z-10" data-testid="select-course">
                  <SelectValue placeholder={t('applyPage.selectCoursePlaceholder')} />
                </SelectTrigger>
                <SelectContent className="z-50 max-h-80" position="popper" sideOffset={4}>
                  {courses.map((course) => {
                    const typeLabel = course.courseType === 'on-demand' ? `【${t('courses.onDemand')}】` 
                      : course.courseType === 'hybrid' ? `【${t('courses.hybrid')}】` 
                      : `【${t('courses.inPerson')}】`;
                    return (
                      <SelectItem 
                        key={course.id} 
                        value={course.id}
                        data-testid={`course-option-${course.id}`}
                      >
                        {typeLabel} {course.name[language] || course.name.ja} - ¥{new Intl.NumberFormat('ja-JP').format(course.price)}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Course Option (A/B/C) Selection */}
            {enabledCourseOptions.length > 0 && (
              <div data-testid="course-option-section">
                <Label className="text-gray-700 font-medium">
                  {L('select')} <span className="text-red-500">*</span>
                </Label>
                <div className="mt-2 grid gap-2">
                  {enabledCourseOptions.map((o) => (
                    <label
                      key={o.code}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${formData.courseOption === o.code ? 'border-[#00A651] bg-[#e6f6ec]' : 'border-gray-200 hover:border-gray-300'}`}
                      data-testid={`course-option-${o.code}`}
                    >
                      <span className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="courseOption"
                          value={o.code}
                          checked={formData.courseOption === o.code}
                          onChange={() => setFormData(prev => ({ ...prev, courseOption: o.code }))}
                          data-testid={`course-option-radio-${o.code}`}
                        />
                        <span className="font-medium">{o.code}{L('suffix')}</span>
                      </span>
                      <span className="text-sm text-gray-600">
                        {L('theory')} {o.theoryHours || 0}h / {L('practical')} {o.practicalHours || 0}h（{L('total')} {o.totalHours || ((o.theoryHours||0)+(o.practicalHours||0))}h）
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Schedule Selection */}
            <div>
              <Label htmlFor="scheduleId" className="text-gray-700 font-medium">
                {t('applyPage.schedule')} <span className="text-red-500">*</span>
              </Label>
              {!formData.courseId ? (
                <div className="mt-1 p-3 bg-gray-50 rounded-lg border border-gray-200 text-gray-500 text-sm">
                  <AlertCircle className="h-4 w-4 inline mr-2" />
                  {t('applyPage.selectCourseFirst')}
                </div>
              ) : availableSchedules.length === 0 ? (
                <div className="mt-1 p-3 bg-yellow-50 rounded-lg border border-yellow-200 text-yellow-700 text-sm">
                  <AlertCircle className="h-4 w-4 inline mr-2" />
                  {t('applyPage.noSchedulesAvailable')}
                </div>
              ) : (
                <Select 
                  value={formData.scheduleId} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, scheduleId: value }))}
                >
                  <SelectTrigger className="mt-1 relative z-10" data-testid="select-schedule">
                    <SelectValue placeholder={t('applyPage.selectSchedulePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent className="z-50 max-h-80" position="popper" sideOffset={4}>
                    {availableSchedules.map((schedule) => (
                      <SelectItem 
                        key={schedule.id} 
                        value={schedule.id}
                        data-testid={`schedule-option-${schedule.id}`}
                      >
                        {formatScheduleDate(schedule.startDate)} ~ {formatScheduleDate(schedule.endDate)}
                        {schedule.location && ` (${schedule.location})`}
                        {` - 残り${schedule.capacity - (schedule.enrolled || 0)}名`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {selectedSchedule && (
                <div className="mt-2 p-3 bg-[#e6f6ec] rounded-lg border border-[#00A651]/20 text-sm">
                  <p className="font-medium text-[#00A651]">
                    {t('applyPage.selectedSchedule')}
                  </p>
                  <p className="text-gray-700 mt-1">
                    {formatScheduleDate(selectedSchedule.startDate)} ~ {formatScheduleDate(selectedSchedule.endDate)}
                  </p>
                  {selectedSchedule.location && (
                    <p className="text-gray-600">
                      {t('applyPage.locationLabel')} {selectedSchedule.location}
                    </p>
                  )}
                  <p className="text-gray-600">
                    {t('applyPage.capacityLabel')} {selectedSchedule.capacity}{t('applyPage.persons')} 
                    ({t('applyPage.available')} {selectedSchedule.capacity - (selectedSchedule.enrolled || 0)}{t('applyPage.persons')})
                  </p>
                </div>
              )}
            </div>

            {/* Experience */}
            <div>
              <Label className="text-gray-700 font-medium">
                {t('apply.form.experience')}
              </Label>
              <RadioGroup
                value={formData.experience}
                onValueChange={(value) => setFormData(prev => ({ ...prev, experience: value }))}
                className="flex gap-6 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="exp-yes" data-testid="radio-exp-yes" />
                  <Label htmlFor="exp-yes" className="font-normal cursor-pointer">
                    {t('apply.form.experienceYes')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="exp-no" data-testid="radio-exp-no" />
                  <Label htmlFor="exp-no" className="font-normal cursor-pointer">
                    {t('apply.form.experienceNo')}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="message" className="text-gray-700 font-medium">
                {t('apply.form.message')}
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className="mt-1"
                data-testid="input-message"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting || !formData.courseId || (availableSchedules.length > 0 && !formData.scheduleId)}
              className="w-full bg-[#00A651] hover:bg-[#008a43] text-white font-bold py-6 disabled:opacity-50"
              data-testid="submit-application"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {t('apply.form.submitting')}
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  {t('apply.form.submit')}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
